/* eslint-disable no-unused-vars */
import { useState, useCallback, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { cloneDeep, isEqual } from 'lodash-es';
import { useSignal, useSignalEffect, useComputed } from '@preact/signals-react';
import { manageAppsAPI } from 'services/manageApps.service';
import { getLocalStorageItem, saveLocalStorageItem } from 'utils';

const useRerender = () => {
  const [, setState] = useState(0);
  return useCallback(() => setState((state) => state + 1), []);
};

function createFilterObject(
  selectedStatus,
  selectedType,
  selectedAppType,
  filterStatusObj,
  filterTypeObj,
  filterAppTypeObj,
  selectedCountries = [],
) {
  const res = {};
  if (selectedStatus === 'allButArchived') {
    res._hide_status_type = filterStatusObj[selectedStatus];
  }
  if (
    selectedStatus !== 'empty' &&
    selectedStatus !== 'allStatuses' &&
    selectedStatus !== 'allButArchived'
  ) {
    res._status_type = filterStatusObj[selectedStatus];
  }
  if (selectedType !== 'empty' && selectedType !== 'allTypes') {
    res.isOnBoarding = filterTypeObj[selectedType];
  }
  if (selectedAppType !== 'allAppTypes') {
    res.isWebsite = filterAppTypeObj[selectedAppType];
  }
  if (selectedCountries.length > 0) {
    res.countries = [...selectedCountries];
  }
  return res;
}

const filterStatusObj = {
  suspended: 8,
  allButArchived: 9,
  active: 0,
  paused: 3,
};

const filterTypeObj = {
  onBoarding: true,
  onGoing: false,
};

const types = {
  allTypes: 'All Campaigns Types',
  onBoarding: 'Onboarding Only',
  onGoing: 'Ongoing Only',
};

const statuses = {
  allStatuses: 'All Campaigns',
  allButArchived: 'All But Archived',
  active: 'Active only',
  paused: 'Paused Only',
  suspended: 'Suspended Only',
};

const appTypes = {
  allAppTypes: 'All',
  app: 'App',
  website: 'Website',
};

const filterAppTypeObj = {
  app: false,
  website: true,
};

const multiSelectedFilterFunc = (filterSettings, items, campaignIds = new Set()) => {
  const hasFilterSettings = Object.keys(filterSettings).length !== 0;
  const hasCampaignIds = campaignIds.size > 0;

  if (!hasFilterSettings && !hasCampaignIds) {
    return items;
  }

  const matchesFilters = (item) => {
    if (!hasFilterSettings) return true;
    return Object.entries(filterSettings).every(([key, value]) => {
      if (key === 'countries' && Array.isArray(value)) {
        const itemCountries = item[key] ? item[key].split(',') : [];
        return value.some((country) => itemCountries.includes(country));
      }
      if (key === '_hide_status_type') {
        return item._status_type !== value;
      }
      return item[key] === value;
    });
  };

  return items.filter((item) => {
    if (hasCampaignIds && !campaignIds.has(item._campaignid)) return false;
    return matchesFilters(item);
  });
};

const getAvailableCountries = (availableCampaigns) => {
  if (!availableCampaigns) return [];
  const res = availableCampaigns
    ?.map(({ countries }) => countries)
    .join(',')
    .split(',')
    .filter(Boolean);
  const uniqueCountries = Array.from(new Set([...res]));
  return uniqueCountries;
};

const checkLocalStorageSelectedItems = (rowData, toggleApp, setRowData) => {
  let selectedAppsLocalStorage = localStorage.getItem('filteredItems');
  let initialDataArr = selectedAppsLocalStorage ? JSON.parse(selectedAppsLocalStorage) : '';
  if (initialDataArr && rowData.length) {
    const promotingCampaigns = rowData.filter((row) => row.pba_number === initialDataArr);
    if (!promotingCampaigns.length) return;
    const _campaignidArray = promotingCampaigns.map(({ _campaignid }) => _campaignid);
    toggleApp(..._campaignidArray);
    setRowData(promotingCampaigns);
    localStorage.removeItem('filteredItems');
  }
};

export const useToggleCampaignsFilter = (
  campaignData$,
  rowData,
  setRowData,
  data,
  initialDataArr = '',
) => {
  const gridRef = useRef(null);
  const currentFilter$ = useSignal('campaignStatus');
  const { all_countries } = manageAppsAPI.useGetCountriesListQuery(undefined, {
    selectFromResult: ({ data }) => {
      let newData = data;
      if (data) {
        newData = Object.values(data)
          .flat()
          .reduce((acc, current) => {
            acc[current.cca2] = current.name;
            return acc;
          }, {});
      }
      return { all_countries: newData };
    },
  });

  const [stateSet] = useState(
    () =>
      new Set(
        getLocalStorageItem({ key: 'tableState', field: 'filteredCampaigns', initialValue: [] }),
      ),
  );
  const state = [...stateSet.values()];

  useLayoutEffect(() => {
    if (stateSet.size && campaignData$.value !== null) {
      const res = campaignData$.value.campaignsData.filter((row) => stateSet.has(row._campaignid));
      setRowData(res);
    }
  }, [stateSet, campaignData$.value]);

  useEffect(() => {
    saveLocalStorageItem({ key: 'tableState', field: 'filteredCampaigns', value: state });
  }, [state]);

  const searchValue$ = useSignal('');
  const showDropDown$ = useSignal(false);

  const rerender = useRerender();

  const appNumberToCampaignNumbers = useMemo(() => {
    const campData = data?.campaignsData ? structuredClone(data.campaignsData) : [];

    if (!campData) return [];
    const res = campData.reduce((acc, current) => {
      const currentAppNumber = current.pba_number;
      const appIsAlreadyContained = campData
        .filter(({ pba_number }) => pba_number === currentAppNumber)
        .map(({ _campaignid }) => _campaignid);
      if (!Object.hasOwn(acc, currentAppNumber)) {
        acc[currentAppNumber] = appIsAlreadyContained;
      }
      return acc;
    }, {});
    return res;
  }, [data?.campaignsData]);

  const appNumberToAppName = useMemo(() => {
    const campData = data?.campaignsData;
    if (!campData) return [];
    const res = campData.reduce((acc, { pba_number, app }) => {
      if (!Object.hasOwn(acc, pba_number)) {
        acc[pba_number] = app;
      }
      return acc;
    }, {});
    return res;
  }, [data?.campaignsData]);

  const campNumberToCampName = useMemo(() => {
    const campData = data?.campaignsData;
    if (!campData) return [];
    const res = campData.reduce((acc, { _campaignid, campaign }) => {
      if (!Object.hasOwn(acc, _campaignid)) {
        acc[_campaignid] = campaign;
      }
      return acc;
    }, {});
    return res;
  }, [data?.campaignsData]);

  const selectedAppsAndCampaigns = useMemo(() => {
    let selectedCampaigns = structuredClone(state);

    let selectedApps = [];
    Object.entries(appNumberToCampaignNumbers).forEach(([appNumber, campNumbers]) => {
      const appIsSelected = campNumbers.every((campNumber) => state.includes(campNumber));
      if (appIsSelected) {
        selectedApps.push(+appNumber);
        campNumbers.forEach((campNumber) => {
          selectedCampaigns = selectedCampaigns.filter((campNum) => campNum !== campNumber);
        });
      }
    });
    return { selectedApps, selectedCampaigns };
  }, [appNumberToCampaignNumbers, state]);

  //console.log(selectedAppsAndCampaigns);

  const isActiveCamp = useCallback((_campaignid) => stateSet.has(_campaignid), [stateSet]);
  const isActiveApp = useCallback(
    (campaignsNumbers) => campaignsNumbers.every((_campaignid) => stateSet.has(_campaignid)),
    [stateSet],
  );

  const selectedStatusFilter$ = useSignal(
    getLocalStorageItem({
      key: 'tableState',
      field: 'statusFilter',
      initialValue: 'allButArchived',
    }),
  );

  const selectedTypeFilter$ = useSignal(
    getLocalStorageItem({
      key: 'tableState',
      field: 'typeFilter',
      initialValue: 'empty',
    }),
  );
  const selectedAppTypeFilter$ = useSignal(
    getLocalStorageItem({
      key: 'tableState',
      field: 'appTypeFilter',
      initialValue: 'allAppTypes',
    }),
  );
  const selectedCountriesFilter$ = useSignal(
    getLocalStorageItem({
      key: 'tableState',
      field: 'countriesFilter',
      initialValue: [],
    }),
  );

  useSignalEffect(() => {
    saveLocalStorageItem({
      key: 'tableState',
      field: 'statusFilter',
      value: selectedStatusFilter$.value,
    });
    saveLocalStorageItem({
      key: 'tableState',
      field: 'typeFilter',
      value: selectedTypeFilter$.value,
    });
    saveLocalStorageItem({
      key: 'tableState',
      field: 'countriesFilter',
      value: selectedCountriesFilter$.value,
    });
    saveLocalStorageItem({
      key: 'tableState',
      field: 'appTypeFilter',
      value: selectedAppTypeFilter$.value,
    });
  });

  const availableCountries$ = useSignal(null);

  const fullNameCountries = useMemo(() => {
    if (availableCountries$.value?.length && all_countries) {
      const fullNameCountries = availableCountries$.value?.map((name) => {
        return { country: all_countries[name], cca2: name };
      });
      return fullNameCountries;
    }
  }, [availableCountries$.value, all_countries]);

  useEffect(() => {
    availableCountries$.value = getAvailableCountries(rowData);
  }, [rowData]);

  const getRowDataToUpdate = useCallback(
    (objToFilter) => {
      if (data?.campaignsData) {
        const copyRowData = [...data.campaignsData];
        return multiSelectedFilterFunc(objToFilter, copyRowData, stateSet);
      }
      return [];
    },
    [selectedTypeFilter$.value, selectedStatusFilter$.value, data?.campaignsData, stateSet],
  );

  const toggleCamp = useCallback(
    (...campaignsNumbers) => {
      campaignsNumbers.forEach((_campaignid) =>
        isActiveCamp(_campaignid) ? stateSet.delete(_campaignid) : stateSet.add(_campaignid),
      );
      rerender();
    },
    [isActiveCamp],
  );

  const toggleApp = useCallback(
    (...campaignsNumbers) => {
      if (isActiveApp(campaignsNumbers)) {
        campaignsNumbers.forEach((_campaignid) => stateSet.delete(_campaignid));
      } else {
        campaignsNumbers.forEach((_campaignid) => stateSet.add(_campaignid));
      }
      rerender();
    },
    [isActiveApp],
  );

  const handleDeselectCampaigns = useCallback(
    (campaignsNumbers) => {
      campaignsNumbers.forEach((_campaignid) => stateSet.delete(_campaignid));
      rerender();
    },
    [stateSet],
  );
  const clearAll = useCallback(() => {
    stateSet.clear();
    setRowData(data.campaignsData);
    campaignData$.value.campaignsData = data.campaignsData;
    selectedStatusFilter$.value = 'empty';
    selectedTypeFilter$.value = 'empty';
    selectedAppTypeFilter$.value = 'allAppTypes';
    selectedCountriesFilter$.value = [];
  }, [data?.campaignsData]);

  const filterByStatus = useCallback(
    (filteredStatus) => {
      if (selectedStatusFilter$.value === filteredStatus) return;
      selectedStatusFilter$.value = filteredStatus;
      const objToFilter = createFilterObject(
        filteredStatus,
        selectedTypeFilter$.value,
        selectedAppTypeFilter$.value,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        selectedCountriesFilter$.value,
      );
      const newRowData = multiSelectedFilterFunc(objToFilter, data.campaignsData, stateSet);
      campaignData$.value.campaignsData = newRowData;
      setRowData(newRowData);
    },
    [selectedTypeFilter$.value, selectedAppTypeFilter$.value, data?.campaignsData],
  );

  const filterByType = useCallback(
    (filteredType) => {
      if (selectedTypeFilter$.value === filteredType) return;
      selectedTypeFilter$.value = filteredType;
      const objToFilter = createFilterObject(
        selectedStatusFilter$.value,
        filteredType,
        selectedAppTypeFilter$.value,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        selectedCountriesFilter$.value,
      );

      const newRowData = multiSelectedFilterFunc(objToFilter, data.campaignsData, stateSet);
      campaignData$.value.campaignsData = [...newRowData];
      setRowData(newRowData);
    },
    [selectedStatusFilter$.value, selectedAppTypeFilter$.value, data?.campaignsData],
  );

  const filterByAppType = useCallback(
    (filteredAppType) => {
      if (selectedAppTypeFilter$.value === filteredAppType) return;
      selectedAppTypeFilter$.value = filteredAppType;
      const objToFilter = createFilterObject(
        selectedStatusFilter$.value,
        selectedTypeFilter$.value,
        filteredAppType,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        selectedCountriesFilter$.value,
      );
      const newRowData = multiSelectedFilterFunc(objToFilter, data.campaignsData, stateSet);
      campaignData$.value.campaignsData = [...newRowData];
      setRowData(newRowData);
    },
    [
      selectedStatusFilter$.value,
      selectedTypeFilter$.value,
      selectedAppTypeFilter$.value,
      data?.campaignsData,
    ],
  );

  const handleClickOutsideDropDown = useCallback(
    (showDropDown$) => {
      const res = campaignData$.value.campaignsData.filter((row) => stateSet.has(row._campaignid));

      //showDropDown$.value = false;
      if (res.length) setRowData(res);
      if (!res.length) {
        const objToFilter = createFilterObject(
          selectedStatusFilter$.value,
          selectedTypeFilter$.value,
          selectedAppTypeFilter$.value,
          filterStatusObj,
          filterTypeObj,
          filterAppTypeObj,
          selectedCountriesFilter$.value,
        );
        const newRowData = multiSelectedFilterFunc(objToFilter, data.campaignsData, res);
        setRowData(newRowData);
      }
    },
    [
      stateSet,
      rowData,
      selectedStatusFilter$.value,
      selectedTypeFilter$.value,
      selectedAppTypeFilter$.value,
    ],
  );

  const updateGrid = useCallback(
    (filteredCamp, campsToDelete) => {
      const filteredObj = createFilterObject(
        selectedStatusFilter$.value,
        selectedTypeFilter$.value,
        selectedAppTypeFilter$.value,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        selectedCountriesFilter$.value,
      );
      const resultCampNumbers = filteredCamp.filter((camp) => !campsToDelete.includes(camp));

      if (!resultCampNumbers.length) {
        const copyRowData = [...data.campaignsData];
        const newRowData = multiSelectedFilterFunc(filteredObj, copyRowData, undefined);
        setRowData(newRowData);
        campaignData$.value.campaignsData = newRowData;
        return;
      }
      const dataToUpdate = getRowDataToUpdate(filteredObj).filter((row) => {
        return resultCampNumbers.includes(row._campaignid);
      });
      setRowData(dataToUpdate);
      //campaignData$.value.campaignsData = dataToUpdate;
    },
    [
      campaignData$.value?.campaignsData,
      selectedStatusFilter$.value,
      selectedTypeFilter$.value,
      selectedAppTypeFilter$.value,
    ],
  );

  const deleteSelectedItem = useCallback(
    (appOrCampNumber) => {
      const { appNumber, campNumber } = appOrCampNumber;
      if (campNumber) {
        handleDeselectCampaigns([campNumber]);
        updateGrid(state, [campNumber]);
      }
      if (appNumber) {
        const campsToDelete = appNumberToCampaignNumbers[appNumber.toString()];
        handleDeselectCampaigns(campsToDelete);
        updateGrid(state, campsToDelete);
      }
    },
    [appNumberToCampaignNumbers, state],
  );

  const handleDeselectCountry = useCallback(
    (countryName) => {
      const newCountriesList = selectedCountriesFilter$.value.filter(
        (country) => country !== countryName,
      );
      selectedCountriesFilter$.value = [...newCountriesList];
      const objToFilter = createFilterObject(
        selectedStatusFilter$.value,
        selectedTypeFilter$.value,
        selectedAppTypeFilter$.value,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        newCountriesList,
      );
      const newRowData = getRowDataToUpdate(objToFilter);
      setRowData(newRowData);
      campaignData$.value.campaignsData = newRowData;
    },
    [
      selectedCountriesFilter$.value,
      getRowDataToUpdate,
      selectedStatusFilter$.value,
      selectedTypeFilter$.value,
      selectedAppTypeFilter$.value,
    ],
  );

  const handleApplyClick = useCallback(
    (countries) => {
      selectedCountriesFilter$.value = [...countries];
      const objToFilter = createFilterObject(
        selectedStatusFilter$.value,
        selectedTypeFilter$.value,
        selectedAppTypeFilter$.value,
        filterStatusObj,
        filterTypeObj,
        filterAppTypeObj,
        countries,
      );
      const newRowData = multiSelectedFilterFunc(objToFilter, data.campaignsData, stateSet);
      campaignData$.value.campaignsData = [...newRowData];
      setRowData(newRowData);
    },
    [selectedStatusFilter$.value, selectedTypeFilter$.value, data?.campaignsData],
  );

  const deleteSelectedStatusFilter = useCallback(() => {
    selectedStatusFilter$.value = 'empty';
    const filteredObj = createFilterObject(
      'empty',
      selectedTypeFilter$.value,
      selectedAppTypeFilter$.value,
      filterStatusObj,
      filterTypeObj,
      filterAppTypeObj,
      selectedCountriesFilter$.value,
    );
    let res = [];
    if (stateSet.size) {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, stateSet);
    } else {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, undefined);
    }
    setRowData(res);
    campaignData$.value.campaignsData = res;
  }, [stateSet, getRowDataToUpdate]);

  const deleteSelectedTypeFilter = useCallback(() => {
    selectedTypeFilter$.value = 'empty';
    const filteredObj = createFilterObject(
      selectedStatusFilter$.value,
      'empty',
      selectedAppTypeFilter$.value,
      filterStatusObj,
      filterTypeObj,
      filterAppTypeObj,
      selectedCountriesFilter$.value,
    );
    let res = [];
    if (stateSet.size) {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, stateSet);
    } else {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, undefined);
    }
    setRowData(res);
    campaignData$.value.campaignsData = res;
  }, [stateSet, getRowDataToUpdate]);

  const deleteSelectedAppTypeFilter = useCallback(() => {
    selectedAppTypeFilter$.value = 'allAppTypes';
    const filteredObj = createFilterObject(
      selectedStatusFilter$.value,
      selectedTypeFilter$.value,
      'allAppTypes',
      filterStatusObj,
      filterTypeObj,
      filterAppTypeObj,
      selectedCountriesFilter$.value,
    );
    let res = [];
    if (stateSet.size) {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, stateSet);
    } else {
      res = multiSelectedFilterFunc(filteredObj, data.campaignsData, undefined);
    }
    setRowData(res);
    campaignData$.value.campaignsData = res;
  }, [stateSet, getRowDataToUpdate]);

  const updateTableData = useCallback((data) => {
    const { newValue, fieldName, campId } = data;
    setRowData((currentRowData) => {
      return currentRowData.map((row) => {
        if (row.campaignId === campId) {
          return { ...row, [fieldName]: newValue };
        }
        return row;
      });
    });
  }, []);

  return {
    filterByStatus,
    filterByType,
    filterByAppType,
    isActiveCamp,
    state,
    toggleCamp,
    toggleApp,
    isActiveApp,
    selectedAppsAndCampaigns,
    appNumberToCampaignNumbers,
    handleDeselectCampaigns,
    selectedStatusFilter$,
    selectedTypeFilter$,
    selectedAppTypeFilter$,
    handleClickOutsideDropDown,
    searchValue$,
    showDropDown$,
    deleteSelectedItem,
    deleteSelectedStatusFilter,
    deleteSelectedTypeFilter,
    deleteSelectedAppTypeFilter,
    fullNameCountries,
    selectedCountriesFilter$,
    handleApplyClick,
    all_countries,
    handleDeselectCountry,
    clearAll,
    types,
    statuses,
    appTypes,
    appNumberToAppName,
    campNumberToCampName,
    checkLocalStorageSelectedItems,
    gridRef,
    updateTableData,
    currentFilter$,
  };
};
